import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { ArrowLeft, Save, X } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import {
  Admin,
  AdminUpdatePayload,
} from '../../../common/models/general-settings'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import {
  getGeneralSettingsAdminById,
  updateAdmin,
} from '../../../core/services/general-settings'
import { MessageBox } from '../messages/MessageBox'

interface IAdmins {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  lastActivity: string
  active: boolean
  password?: string | null
}

export const AdminSettingsPage: React.FC = () => {
  const [admin, setAdmin] = useState<Admin>()
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const navigate = useNavigate()
  const { adminId } = useParams()

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        if (adminId) {
          const resp = await getGeneralSettingsAdminById(adminId)
          if (resp.status === 200) {
            setAdmin(resp.data)
            setValue('id', resp.data.id)
            setValue('firstName', resp.data.firstName)
            setValue('lastName', resp.data.lastName)
            setValue('email', resp.data.email)
            setValue('phone', resp.data.phone)
            setValue('lastActivity', formatDateTime(resp.data.lastActivityDate))
            setValue('active', resp.data.active)
            setValue('password', '')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const schema = yup.object().shape({
    id: yup.string().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().required(),
    phone: yup.string().required(),
    lastActivity: yup.string().required(),
    active: yup.boolean().required(),
    password: yup
      .string()
      .notRequired()
      .min(6)
      .nullable()
      .transform((value) => (!!value ? value : null)),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm<IAdmins>({
    mode: 'onChange',
    resolver: yupResolver<IAdmins>(schema),
    defaultValues: {
      password: null,
    },
  })

  const submit = async (values: IAdmins) => {
    try {
      setLoading(true)
      if (admin) {
        const data: AdminUpdatePayload = {
          phone: values.phone,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          active: values.active,
        }
        if (values.password) {
          data.password = values.password
        }
        const response = await updateAdmin(admin.id, data)
        if (response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (admin) {
      setValue('id', admin.id)
      setValue('firstName', admin.firstName)
      setValue('lastName', admin.lastName)
      setValue('phone', admin.phone)
      setValue('email', admin.email)
      setValue('active', admin.active)
      setValue('password', '')
    }
  }

  return (
    <>
      <Header
        pageTitle="Admin Settings"
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSubmit(submit)} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            className="text-monochrome-1 hover:text-accent-1"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <PageTitle
            title={
              admin ? `${admin.firstName} ${admin.lastName}` : 'Add New Admin'
            }
          />
        </div>
        <form
          id="settings-email-form"
          onSubmit={handleSubmit(submit)}
          className="flex flex-col gap-4"
        >
          <FormGroup label="First Name" error={errors?.firstName?.message}>
            <Input
              {...register('firstName')}
              type="text"
              placeholder="Enter Name"
              className={errors?.firstName?.message ? 'border-accent-3' : ''}
            />
          </FormGroup>
          <FormGroup label="Last Name" error={errors?.lastName?.message}>
            <Input
              {...register('lastName')}
              type="text"
              placeholder="Enter Name"
              className={errors?.lastName?.message ? 'border-accent-3' : ''}
            />
          </FormGroup>
          <FormGroup label="Phone Number" error={errors?.phone?.message}>
            <Input
              {...register('phone')}
              type="tel"
              placeholder="Enter Phone Number"
              className={errors?.phone?.message ? 'border-accent-3' : ''}
            />
          </FormGroup>
          <FormGroup label="Email" error={errors?.email?.message}>
            <Input
              {...register('email')}
              type="email"
              placeholder="Enter Email"
              className={errors?.email?.message ? 'border-accent-3' : ''}
            />
          </FormGroup>
          <FormGroup label="Password" error={errors?.password?.message}>
            <Input
              {...register('password')}
              type="password"
              placeholder="Enter Password"
              className={errors?.password?.message ? 'border-accent-3' : ''}
            />
          </FormGroup>
          <FormGroup label="Last Activity">
            <div className="text-monochrome-2">
              {admin?.lastActivityDate
                ? formatDateTime(admin.lastActivityDate)
                : '-'}
            </div>
          </FormGroup>
          <FormGroup label="Active">
            <Checkbox
              {...register('active')}
              checked={admin?.active}
              onCheckedChange={(checked) =>
                setValue('active', Boolean(checked))
              }
            />
          </FormGroup>
        </form>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-monochrome-120">
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button form="settings-email-form" type="submit" disabled={loading}>
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
